import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Pagination } from '../../components/ui/Pagination';
import { showSuccess, showError } from '../../components/ui/Toast';
import { mockBatches } from '../../data/mockData';
import { PaymentBatch } from '../../types';
interface CheckerReviewProps {
  batchId: string;
  onBack: () => void;
  onComplete: () => void;
}
export function CheckerReview({
  batchId,
  onBack,
  onComplete
}: CheckerReviewProps) {
  const [batch, setBatch] = useState<PaymentBatch | undefined>(mockBatches.find(b => b.id === batchId));
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');
  const itemsPerPage = 10;
  if (!batch) return <div data-id="element-304">Batch not found</div>;
  const handleApprove = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setShowApproveConfirm(false);
      showSuccess('Batch Approved Successfully', 'Payment initiation process started.');
      setBatch(prev => prev ? {
        ...prev,
        status: 'PaymentInitiated'
      } : undefined);
      onComplete();
    }, 1500);
  };
  const handleReject = () => {
    if (!rejectionReason.trim()) {
      showError('Rejection reason required', 'Please provide a reason for rejection.');
      return;
    }
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setShowRejectModal(false);
      showSuccess('Batch Rejected', 'The maker has been notified.');
      setBatch(prev => prev ? {
        ...prev,
        status: 'Rejected',
        rejectionReason
      } : undefined);
      onComplete();
    }, 1500);
  };
  const isPending = batch.status === 'ApprovalPending';
  // Compute counts for tabs
  const successRecords = batch.records.filter(r => r.status !== 'Failed' && r.status !== 'Rejected');
  const failedRecords = batch.records.filter(r => r.status === 'Failed' || r.status === 'Rejected');
  const filteredRecords = batch.records.filter(r => {
    if (filter === 'success') return r.status !== 'Failed' && r.status !== 'Rejected';
    if (filter === 'failed') return r.status === 'Failed' || r.status === 'Rejected';
    return true;
  });
  // Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const tabItems = [{
    key: 'all' as const,
    label: 'All Records',
    count: batch.records.length
  }, {
    key: 'success' as const,
    label: 'Successful',
    count: successRecords.length
  }, {
    key: 'failed' as const,
    label: 'Failed',
    count: failedRecords.length
  }];
  return <div className="space-y-6" data-id="element-305">
      <div className="flex items-center justify-between" data-id="element-306">
        <div className="flex items-center space-x-4" data-id="element-307">
          <Button variant="ghost" size="sm" onClick={onBack} data-id="element-308">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-309" />
            Back
          </Button>
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-310">
            Batch Review: {batch.fileName}
          </h2>
          <StatusBadge status={batch.status} data-id="element-311" />
        </div>

        {isPending && <div className="flex space-x-3" data-id="element-312">
            <Button variant="danger" onClick={() => setShowRejectModal(true)} leftIcon={<XCircle className="w-4 h-4" data-id="element-314" />} data-id="element-313">
              Reject Batch
            </Button>
            <Button variant="success" onClick={() => setShowApproveConfirm(true)} leftIcon={<CheckCircle className="w-4 h-4" data-id="element-316" />} data-id="element-315">
              Approve Batch
            </Button>
          </div>}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-317">
        <Card className="p-4" data-id="element-318">
          <p className="text-sm text-gray-500" data-id="element-319">Uploaded By</p>
          <p className="font-medium text-gray-900" data-id="element-320">Bilal Ahmed (Maker)</p>
          <p className="text-xs text-gray-400" data-id="element-321">
            {new Date(batch.uploadDate).toLocaleString()}
          </p>
        </Card>
        <Card className="p-4" data-id="element-322">
          <p className="text-sm text-gray-500" data-id="element-323">Total Records</p>
          <p className="font-medium text-gray-900" data-id="element-324">{batch.totalRecords}</p>
        </Card>
        <Card className="p-4" data-id="element-325">
          <p className="text-sm text-gray-500" data-id="element-326">Total Amount</p>
          <p className="font-medium text-gray-900 text-xl" data-id="element-327">
            PKR {batch.totalAmount.toLocaleString()}
          </p>
        </Card>
        <Card className="p-4" data-id="element-328">
          <p className="text-sm text-gray-500" data-id="element-329">Validation Status</p>
          <div className="flex items-center text-green-600 font-medium" data-id="element-330">
            <CheckCircle className="w-4 h-4 mr-1" data-id="element-331" />
            {batch.validRecords} of {batch.totalRecords} Valid
          </div>
        </Card>
      </div>

      {/* Rejection Reason Display if Rejected */}
      {batch.status === 'Rejected' && batch.rejectionReason && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start" data-id="element-332">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3" data-id="element-333" />
          <div data-id="element-334">
            <h4 className="text-sm font-bold text-red-800" data-id="element-335">Rejection Reason</h4>
            <p className="text-sm text-red-700 mt-1" data-id="element-336">{batch.rejectionReason}</p>
          </div>
        </div>}

      <Card title="Payment Records" data-id="element-337">
        <div className="border-b border-gray-200 mb-4" data-id="element-338">
          <nav className="-mb-px flex space-x-6 px-6" data-id="element-339">
            {tabItems.map(tab => <button key={tab.key} onClick={() => {
            setFilter(tab.key);
            setCurrentPage(1);
          }} className={`
                  whitespace-nowrap py-3.5 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                  ${filter === tab.key ? 'border-[#1B6B3A] text-[#1B6B3A]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `} data-id="element-340">
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${filter === tab.key ? 'bg-[#1B6B3A]/10 text-[#1B6B3A]' : 'bg-gray-100 text-gray-500'}`} data-id="element-341">
                  {tab.count}
                </span>
              </button>)}
          </nav>
        </div>

        <div className="overflow-x-auto" data-id="element-342">
          <DataTable columns={[{
          header: 'Receiver IBAN',
          accessor: r => <span className="font-mono text-xs" data-id="element-344">{r.receiverIban}</span>
        }, {
          header: 'Beneficiary Name',
          accessor: 'receiverName'
        }, {
          header: 'Account Title',
          accessor: r => r.titleFetchResult ? <span className="font-semibold text-green-700 text-xs" data-id="element-345">
                      {r.titleFetchResult}
                    </span> : <span className="text-gray-400 text-xs italic" data-id="element-346">—</span>
        }, {
          header: 'Amount (PKR)',
          accessor: r => <span className="font-mono" data-id="element-347">{r.amount.toLocaleString()}</span>
        }, {
          header: 'Purpose',
          accessor: r => <span className="font-mono text-xs" data-id="element-348">{r.paymentPurpose}</span>
        }, {
          header: 'Purpose Description',
          accessor: r => <div className="max-w-[160px] truncate" title={r.purposeDescription} data-id="element-349">
                    {r.purposeDescription}
                  </div>
        }, {
          header: 'Narration',
          accessor: r => <div className="max-w-[140px] truncate" title={r.narration} data-id="element-350">
                    {r.narration}
                  </div>
        }, {
          header: 'Payment Ref',
          accessor: r => <span className="text-xs text-gray-600" data-id="element-351">
                    {r.paymentReference || '—'}
                  </span>
        }, {
          header: 'Status',
          accessor: r => <StatusBadge status={r.status} size="sm" data-id="element-352" />
        }]} data={paginatedRecords} emptyMessage={`No ${filter === 'all' ? '' : filter} records found`} data-id="element-343" />
        </div>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-353" />
      </Card>

      {/* Approve Confirmation */}
      <ConfirmDialog open={showApproveConfirm} title="Approve Payment Batch" message={`Are you sure you want to approve this batch of ${batch.totalRecords} records totaling PKR ${batch.totalAmount.toLocaleString()}? This will initiate the payment process immediately.`} onConfirm={handleApprove} onCancel={() => setShowApproveConfirm(false)} confirmLabel="Yes, Approve & Pay" variant="success" data-id="element-354" />

      {/* Reject Modal */}
      {showRejectModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-355">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-356">
            <div className="flex items-center mb-4" data-id="element-357">
              <div className="bg-red-100 p-2 rounded-full mr-3" data-id="element-358">
                <XCircle className="w-6 h-6 text-red-600" data-id="element-359" />
              </div>
              <h3 className="text-xl font-bold text-gray-900" data-id="element-360">Reject Batch</h3>
            </div>

            <p className="text-gray-500 mb-4" data-id="element-361">
              Please provide a reason for rejecting this batch. This will be
              sent to the maker.
            </p>

            <textarea className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 min-h-[100px]" placeholder="Enter rejection reason..." value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} data-id="element-362" />

            <div className="flex justify-end space-x-3 mt-6" data-id="element-363">
              <Button variant="outline" onClick={() => setShowRejectModal(false)} data-id="element-364">
                Cancel
              </Button>
              <Button variant="danger" onClick={handleReject} isLoading={isProcessing} data-id="element-365">
                Reject Batch
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}